import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "../../../AppContext";
import {
  SEASON_TYPES,
  isBusinessDate,
  previousSeason,
  validateSeason,
} from "./seasonDates";
import "./seasonCalendar.scss";

const label = (type) => type.replace(/_/g, " ");
const message = (error) =>
  error.response?.data?.message ||
  error.message ||
  "Season calendar unavailable.";
const lunar = (season) =>
  ["ramadan", "eid_fitr", "eid_adha"].includes(season.type);

const businessDate = (value) => {
  if (isBusinessDate(value)) return value;
  const date = new Date(value);
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T/.test(value) ||
    Number.isNaN(date.getTime())
  ) {
    throw new Error("Invalid season business date.");
  }
  return date.toISOString().slice(0, 10);
};

function useCalendar(brandId) {
  const { user, chimeraserv } = useAppContext();
  const token = user?.data?.usrKey;
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({
    seasons: [],
    loading: false,
    error: "",
  });
  useEffect(() => {
    if (!brandId || !token) return undefined;
    const controller = new AbortController();
    setState({ seasons: [], loading: true, error: "", brandId });
    axios
      .get(`${chimeraserv.urlGet}/seasons/${encodeURIComponent(brandId)}`, {
        headers: { Authorization: token },
        signal: controller.signal,
      })
      .then(({ data }) => {
        const records = Array.isArray(data)
          ? data
          : (data?.seasons ?? (data?._id ? [data] : null));
        if (!Array.isArray(records)) {
          throw new Error("Invalid season calendar response.");
        }
        const seasons = records.map((season) => {
          const start = businessDate(season.startDate ?? season.start);
          return {
            ...season,
            id: String(season.id ?? season._id),
            brandId: season.braKey ?? season.brandId,
            start,
            end: businessDate(season.endDate ?? season.end),
            notes: season.note ?? season.notes ?? "",
            year: season.year ?? Number(start?.slice(0, 4)),
          };
        });
        setState({
          seasons,
          timeZone: data.timeZone || "Asia/Qatar",
          permissions: data.permissions ?? { edit: true, confirm: false },
          loading: false,
          error: "",
          brandId,
        });
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          const notFound = error.response?.status === 404;
          setState({
            seasons: [],
            loading: false,
            notFound,
            brandId,
            error: notFound
              ? "Season calendar not found. You can create the first season. If saving also returns 404, the backend season routes must be implemented."
              : message(error),
          });
        }
      });
    return () => controller.abort();
  }, [brandId, token, chimeraserv.urlGet, revision]);
  return {
    ...(state.brandId === brandId
      ? state
      : { seasons: [], loading: !!brandId, error: "" }),
    reload: () => setRevision((value) => value + 1),
  };
}

export function SeasonTabs({ brandId, value, onChange }) {
  const calendar = useCalendar(brandId);
  return (
    <section className="menuGroupTabs menuSeasonTabs" aria-label="Seasons">
      <div className="menuGroupTabRow">
        <h2 className="menuGroupTabLabel">Seasons</h2>
        <div role="group" aria-label="Filter by season">
          <button
            type="button"
            className={!value ? "active" : ""}
            aria-pressed={!value}
            onClick={() => onChange(null, calendar.timeZone)}
          >
            Report dates
          </button>
          {calendar.seasons.map((season) => (
            <button
              type="button"
              key={season.id}
              className={value === season.id ? "active" : ""}
              aria-pressed={value === season.id}
              onClick={() => onChange(season, calendar.timeZone)}
            >
              {season.name} {season.year}
              {/* <span className="menuSeasonDates">{season.start} - {season.end}</span> */}
            </button>
          ))}
        </div>
      </div>
      {!brandId && <p role="status">Select one brand to view seasons.</p>}
      {calendar.loading && <p role="status">Loading seasons...</p>}
      {calendar.error && (
        <p role="alert">
          {calendar.error}{" "}
          <button type="button" onClick={calendar.reload}>
            Retry
          </button>
        </p>
      )}
      {brandId &&
        !calendar.loading &&
        !calendar.error &&
        !calendar.seasons.length && <p role="status">No seasons available.</p>}
    </section>
  );
}

export function SeasonPicker({ brandId, value, onChange, allowComparison }) {
  const calendar = useCalendar(brandId);
  const selected = calendar.seasons.find(
    (season) => season.id === value?.selected.id
  );
  const previous = selected ? previousSeason(calendar.seasons, selected) : null;
  return (
    <div className="seasonPicker">
      <label>
        Season
        <select
          aria-label="Season"
          disabled={!brandId || calendar.loading || !!calendar.error}
          value={selected?.id || ""}
          onChange={(event) => {
            const season = calendar.seasons.find(
              (entry) => entry.id === event.target.value
            );
            onChange(
              season
                ? {
                    selected: season,
                    timeZone: calendar.timeZone,
                    previous: null,
                  }
                : null
            );
          }}
        >
          <option value="">
            {!brandId
              ? "Select one brand"
              : calendar.loading
                ? "Loading seasons..."
                : "Custom dates / presets"}
          </option>
          {calendar.seasons.map((season) => (
            <option key={season.id} value={season.id}>
              {season.name} {season.year} ({season.start} - {season.end})
            </option>
          ))}
        </select>
      </label>
      {calendar.error && (
        <div role="alert">
          {calendar.error}{" "}
          <button type="button" onClick={calendar.reload}>
            Retry
          </button>
        </div>
      )}
      {brandId &&
        !calendar.loading &&
        !calendar.error &&
        !calendar.seasons.length && <p>No seasons available.</p>}
      {selected && (
        <p>
          {selected.start} - {selected.end} ({calendar.timeZone})
          {lunar(selected) && selected.confirmationStatus !== "confirmed"
            ? " / Provisional"
            : ""}
        </p>
      )}
      {selected && allowComparison && (
        <label className="seasonComparison">
          <input
            type="checkbox"
            checked={!!value?.previous}
            disabled={!previous}
            onChange={(event) =>
              onChange({
                selected,
                timeZone: calendar.timeZone,
                previous: event.target.checked ? previous : null,
              })
            }
          />
          Same season last year
          {previous ? (
            <small>
              {previous.name}: {previous.start} - {previous.end}
            </small>
          ) : (
            <small>No unique previous-year season</small>
          )}
        </label>
      )}
      <Link to="/app/settings/seasons">Manage season calendar</Link>
    </div>
  );
}

export default function SeasonCalendar() {
  const { user, chimeraserv } = useAppContext();
  const token = user?.data?.usrKey;
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [brandError, setBrandError] = useState("");
  const [brandLoading, setBrandLoading] = useState(true);
  const [brandRevision, setBrandRevision] = useState(0);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const calendar = useCalendar(brandId);

  useEffect(() => {
    if (!token) return undefined;
    const controller = new AbortController();
    setBrandLoading(true);
    setBrandError("");
    axios
      .get(`${chimeraserv.urlGet}/brand`, {
        headers: { Authorization: token },
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Invalid brand response.");
        setBrands(data);
        setBrandId((current) => current || String(data[0]?.braKey || ""));
        setBrandLoading(false);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) {
          setBrandError(message(failure));
          setBrandLoading(false);
        }
      });
    return () => controller.abort();
  }, [token, chimeraserv.urlGet, brandRevision]);

  const changeBrand = (event) => {
    setBrandId(event.target.value);
    setDraft(null);
    setError("");
    setNotice("");
  };
  const write = async (method, path, body) => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await axios[method](`${chimeraserv.urlPost}${path}`, body, {
        headers: { Authorization: token, "Content-Type": "application/json" },
      });
      setDraft(null);
      setNotice("Season saved.");
      calendar.reload();
    } catch (failure) {
      setError(message(failure));
    } finally {
      setSaving(false);
    }
  };
  const save = (event) => {
    event.preventDefault();
    const validation = validateSeason(draft);
    if (validation) {
      setError(validation);
      return;
    }
    const { name, type, start, end, notes } = draft;
    write(
      draft.id ? "put" : "post",
      draft.id ? `/season/${encodeURIComponent(draft.id)}` : "/season/create",
      {
        braKey: brandId,
        name: name.trim(),
        type,
        startDate: start,
        endDate: end,
        note: notes || "",
      }
    );
  };
  const field = (key) => ({
    value: draft[key] || "",
    onChange: (event) =>
      setDraft({
        ...draft,
        [key]: event.target.value,
        ...(key === "start"
          ? { year: Number(event.target.value.slice(0, 4)) }
          : {}),
      }),
  });
  const years = [
    ...new Set([
      new Date().getFullYear(),
      ...calendar.seasons.map((season) => Number(season.year)),
    ]),
  ].sort((first, second) => first - second);
  const visible = calendar.seasons
    .filter((season) => String(season.year) === year)
    .sort((first, second) => first.start.localeCompare(second.start));
  const lunarDatesLocked = draft && draft.source === "chimera" && lunar(draft);

  return (
    <section className="seasonCalendar">
      <header>
        <h1>Season Calendar</h1>
        <div className="seasonToolbar">
          <label>
            Brand
            <select
              aria-label="Brand"
              value={brandId}
              disabled={saving || brandLoading}
              onChange={changeBrand}
            >
              <option value="">
                {brandLoading ? "Loading brands..." : "Select brand"}
              </option>
              {brands.map((brand) => (
                <option key={brand.braKey} value={brand.braKey}>
                  {brand.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Year
            <select
              aria-label="Year"
              value={year}
              onChange={(event) => setYear(event.target.value)}
            >
              {years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={
              !brandId ||
              (!calendar.permissions?.edit && !calendar.notFound) ||
              saving ||
              calendar.loading
            }
            onClick={() => {
              setError("");
              setNotice("");
              setDraft({
                name: "",
                type: "custom",
                start: `${year}-01-01`,
                end: `${year}-01-01`,
                year: Number(year),
                notes: "",
              });
            }}
          >
            <FontAwesomeIcon icon={["fas", "plus"]} /> Add season
          </button>
        </div>
      </header>
      {brandError && (
        <p role="alert">
          {brandError}{" "}
          <button onClick={() => setBrandRevision((value) => value + 1)}>
            Retry
          </button>
        </p>
      )}
      {calendar.loading && <p role="status">Loading calendar...</p>}
      {calendar.error && (
        <p role="alert">
          {calendar.error} <button onClick={calendar.reload}>Retry</button>
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {draft && (
        <form onSubmit={save} className="seasonEditor">
          <h2>{draft.id ? "Edit season" : "New season"}</h2>
          <div className="seasonFields">
            <label>
              Name
              <input required maxLength={120} {...field("name")} />
            </label>
            <label>
              Type
              <select {...field("type")} disabled={draft.source === "chimera"}>
                {SEASON_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {label(type)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Start
              <input
                type="date"
                required
                disabled={lunarDatesLocked}
                {...field("start")}
              />
            </label>
            <label>
              End
              <input
                type="date"
                required
                min={draft.start}
                disabled={lunarDatesLocked}
                {...field("end")}
              />
            </label>
            <label className="seasonNotes">
              Notes
              <textarea maxLength={2000} {...field("notes")} />
            </label>
          </div>
          <div className="seasonActions">
            <button type="submit" disabled={saving}>
              <FontAwesomeIcon icon={["fas", "save"]} />{" "}
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setDraft(null);
                setError("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {!calendar.loading && !calendar.error && brandId && (
        <div className="seasonTableWrap">
          <table>
            <caption>
              {year} / {calendar.timeZone || "Asia/Qatar"}
            </caption>
            <thead>
              <tr>
                <th>Season</th>
                <th>Business dates</th>
                <th>Source / status</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((season) => (
                <tr key={season.id}>
                  <td>
                    <strong>{season.name}</strong>
                    <small>{label(season.type)}</small>
                  </td>
                  <td>
                    {season.start}
                    <br />
                    {season.end}
                  </td>
                  <td>
                    {season.source === "chimera" ? "Chimera" : "Client"}
                    <small>
                      {lunar(season)
                        ? season.confirmationStatus === "confirmed"
                          ? "Confirmed"
                          : "Provisional"
                        : "Scheduled"}
                    </small>
                  </td>
                  <td>{season.notes || ""}</td>
                  <td>
                    <div className="seasonActions">
                      {calendar.permissions?.edit && (
                        <button
                          type="button"
                          title="Edit season"
                          aria-label={`Edit ${season.name}`}
                          disabled={saving}
                          onClick={() => {
                            setDraft({ ...season });
                            setError("");
                            setNotice("");
                          }}
                        >
                          <FontAwesomeIcon icon={["fas", "pen"]} />
                        </button>
                      )}
                      {calendar.permissions?.confirm &&
                        season.source === "chimera" &&
                        lunar(season) &&
                        season.confirmationStatus !== "confirmed" &&
                        [-1, 0, 1].map((shiftDays) => (
                          <button
                            type="button"
                            key={shiftDays}
                            disabled={saving}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Confirm ${season.name} ${season.year}, shifting both dates by ${shiftDays} day(s)?`
                                )
                              ) {
                                write(
                                  "post",
                                  `/brand/${encodeURIComponent(brandId)}/seasons/${encodeURIComponent(season.id)}/confirm`,
                                  { shiftDays, version: season.version }
                                );
                              }
                            }}
                          >
                            {shiftDays === 0
                              ? "Confirm"
                              : `Confirm ${shiftDays > 0 ? "+" : ""}${shiftDays}d`}
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!visible.length && <p>No seasons for {year}.</p>}
        </div>
      )}
    </section>
  );
}
